import React, { createContext, useContext, useState, useEffect } from 'react';

const DriverContext = createContext(null);

export const useDriver = () => {
  const context = useContext(DriverContext);
  if (!context) {
    throw new Error('useDriver must be used within DriverProvider');
  }
  return context;
};

export const DriverProvider = ({ children }) => {
  const [activeRoute, setActiveRoute] = useState(null);
  const [currentStop, setCurrentStop] = useState(null);
  const [loadedPackages, setLoadedPackages] = useState([]);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingPODs, setPendingPODs] = useState([]);
  const [geoLocation, setGeoLocation] = useState(null);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Geolocation tracking
  useEffect(() => {
    if ('geolocation' in navigator) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          setGeoLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          });
        },
        (error) => console.warn('Geolocation error:', error),
        { enableHighAccuracy: true, maximumAge: 30000 }
      );
      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, []);

  // Start a route
  const startRoute = (route) => {
    setActiveRoute({
      ...route,
      startedAt: new Date().toISOString(),
      status: 'IN_PROGRESS'
    });
    setCurrentStop(null);
  };

  // Add loaded package
  const addLoadedPackage = (pkg) => {
    setLoadedPackages(prev => {
      if (prev.some(p => p.id === pkg.id)) return prev;
      return [...prev, { ...pkg, loadedAt: new Date().toISOString() }];
    });
  };

  // Set current stop
  const arriveAtStop = (stop) => {
    setCurrentStop({
      ...stop,
      arrivedAt: new Date().toISOString(),
      status: 'ARRIVED'
    });
  };

  // Complete delivery
  const completeDelivery = (stopId, pod) => {
    setDeliveryHistory(prev => [...prev, {
      stopId,
      pod,
      completedAt: new Date().toISOString(),
      status: 'DELIVERED'
    }]);
    
    if (activeRoute) {
      setActiveRoute(prev => ({
        ...prev,
        stops: prev.stops.map(s => 
          s.id === stopId ? { ...s, status: 'DELIVERED', pod } : s
        )
      }));
    }
    setCurrentStop(null);
  };

  // Mark delivery failed
  const markDeliveryFailed = (stopId, failureData) => {
    setDeliveryHistory(prev => [...prev, {
      stopId,
      ...failureData,
      completedAt: new Date().toISOString(),
      status: 'FAILED'
    }]);
    
    if (activeRoute) {
      setActiveRoute(prev => ({
        ...prev,
        stops: prev.stops.map(s => 
          s.id === stopId ? { ...s, status: 'FAILED', failureData } : s
        )
      }));
    }
    setCurrentStop(null);
  };

  // Queue POD for sync
  const queuePOD = (pod) => {
    setPendingPODs(prev => [...prev, {
      ...pod,
      queuedAt: new Date().toISOString()
    }]);
  };

  // Re-sequence stops (if allowed)
  const resequenceStops = (newOrder) => {
    if (!activeRoute?.allowResequence) return false;
    setActiveRoute(prev => ({
      ...prev,
      stops: newOrder
    }));
    return true;
  };

  // Complete route
  const completeRoute = () => {
    if (activeRoute) {
      setActiveRoute(prev => ({
        ...prev,
        completedAt: new Date().toISOString(),
        status: 'COMPLETED'
      }));
    }
  };

  return (
    <DriverContext.Provider value={{
      activeRoute,
      currentStop,
      loadedPackages,
      deliveryHistory,
      isOnline,
      pendingPODs,
      geoLocation,
      startRoute,
      addLoadedPackage,
      arriveAtStop,
      completeDelivery,
      markDeliveryFailed,
      queuePOD,
      resequenceStops,
      completeRoute,
      setActiveRoute
    }}>
      {children}
    </DriverContext.Provider>
  );
};

export default DriverProvider;
